import { useMemo, useState } from "react";
import { ArrowUp, ArrowDown, ArrowUpDown, Download, Search, SlidersHorizontal } from "lucide-react";
import { EmptyState, SearchEmpty } from "@/components/states";

// PATCH 36 Me7 + Me8 — one standardised table for every admin/dashboard table: text filter,
// click-to-sort headers, a column-visibility menu, and CSV export. Greyscale, keyboard-friendly.
// columns: [{ key, label, render?(row), csv?(row), sortable?=true, hideable?=true }]
function toCsv(cols, rows) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = cols.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) => cols.map((c) => esc(c.csv ? c.csv(r) : r[c.key])).join(",")).join("\n");
  return `${head}\n${body}`;
}

export default function DataTable({ columns, rows = [], testid = "data-table", csvName = "export.csv",
  searchable = true, emptyTitle = "Nothing here yet." }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const [hidden, setHidden] = useState(() => new Set());
  const [menu, setMenu] = useState(false);

  const visibleCols = columns.filter((c) => !hidden.has(c.key));

  const filtered = useMemo(() => {
    let out = rows;
    if (q.trim()) {
      const t = q.toLowerCase();
      out = out.filter((r) => columns.some((c) => String(c.csv ? c.csv(r) : r[c.key] ?? "").toLowerCase().includes(t)));
    }
    if (sort.key) {
      const col = columns.find((c) => c.key === sort.key);
      out = [...out].sort((a, b) => {
        const av = col?.csv ? col.csv(a) : a[sort.key];
        const bv = col?.csv ? col.csv(b) : b[sort.key];
        if (av == null) return 1; if (bv == null) return -1;
        return (av > bv ? 1 : av < bv ? -1 : 0) * sort.dir;
      });
    }
    return out;
  }, [rows, q, sort, columns]);

  const onSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

  const exportCsv = () => {
    const blob = new Blob([toCsv(visibleCols, filtered)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = csvName; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid={testid}>
      <div className="k-no-print" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        {searchable && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flex: 1, minWidth: 180 }}>
            <Search size={13} className="k-muted" />
            <input className="k-control" data-testid={`${testid}-filter`} placeholder="Filter…" value={q}
              onChange={(e) => setQ(e.target.value)} style={{ padding: "7px 10px", fontSize: 13 }} />
          </span>
        )}
        <div style={{ position: "relative" }}>
          <button className="k-btn k-btn-ghost" data-testid={`${testid}-columns`} onClick={() => setMenu((m) => !m)}
            style={{ padding: "8px 12px", fontSize: 13 }}><SlidersHorizontal size={13} /> Columns</button>
          {menu && (
            <div className="k-container" style={{ position: "absolute", right: 0, zIndex: 40, marginTop: 4, padding: 10, width: 200 }}>
              {columns.filter((c) => c.hideable !== false).map((c) => (
                <label key={c.key} className="k-muted" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "4px 0", cursor: "pointer" }}>
                  <input type="checkbox" checked={!hidden.has(c.key)} data-testid={`${testid}-col-${c.key}`}
                    onChange={() => setHidden((h) => { const n = new Set(h); n.has(c.key) ? n.delete(c.key) : n.add(c.key); return n; })} />
                  {c.label}
                </label>
              ))}
            </div>
          )}
        </div>
        <button className="k-btn k-btn-ghost" data-testid={`${testid}-export`} onClick={exportCsv}
          style={{ padding: "8px 12px", fontSize: 13 }}><Download size={13} /> CSV</button>
      </div>
      {filtered.length === 0 ? (
        q ? <SearchEmpty query={q} testid={`${testid}-empty`} /> : <EmptyState title={emptyTitle} testid={`${testid}-empty`} />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="k-table">
            <thead>
              <tr>
                {visibleCols.map((c) => (
                  <th key={c.key}>
                    {c.sortable === false ? c.label : (
                      <button className="k-link k-muted" data-testid={`${testid}-sort-${c.key}`} onClick={() => onSort(c.key)}
                        style={{ display: "inline-flex", alignItems: "center", gap: 4, font: "inherit", fontWeight: 600 }}>
                        {c.label}
                        {sort.key === c.key ? (sort.dir === 1 ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} style={{ opacity: 0.4 }} />}
                      </button>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.id || i} data-testid={`${testid}-row-${i}`}>
                  {visibleCols.map((c) => <td key={c.key}>{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
